// src/components/reader/LessonBottomBar.jsx
//
// Mobile-only inline lesson controls beside Save / Notes: Previous, Sections,
// Mark complete and Next. They scroll with the page and never cover lesson text.
//
// It is deliberately dumb: the reader decides what Prev/Next mean (flattenLessonOrder
// + prevNextFor) and owns the completion toggle. This file only draws the bar and
// stays out of the way -- the caller hides it during a practice/essay view and while
// a drawer is open, so it can never cover the essay textarea or a Submit button.
import React from 'react';
import { IconCheck } from '../icons.jsx';
import './lesson.css';

function Chevron({ dir }) {
    return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points={dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
        </svg>
    );
}

export default function LessonBottomBar({
    onPrev, onNext, prevLabel, nextLabel,
    complete, onToggleComplete, showComplete = true,
    onOpenSections,
}) {
    return (
        <div className="btl-lessonbar lg:hidden">
            <div className="flex items-center gap-1 px-2 py-2">
                <button type="button" onClick={onPrev} disabled={!onPrev}
                    aria-label={prevLabel ? 'Previous: ' + prevLabel : 'Previous'}
                    className="flex items-center justify-center w-11 h-11 rounded-full text-book-muted disabled:opacity-30 enabled:hover:text-book-accent transition-colors">
                    <Chevron dir="left" />
                </button>

                <button type="button" onClick={onOpenSections}
                    className="flex-grow min-w-0 px-3 h-11 font-display text-sm font-semibold text-book-muted hover:text-book-accent transition-colors">
                    Sections
                </button>

                {showComplete ? (
                    <button type="button" onClick={onToggleComplete} aria-pressed={!!complete}
                        className={'shrink-0 px-3 h-11 rounded-full font-display text-xs font-semibold inline-flex items-center justify-center gap-1 transition-colors '
                            + (complete
                                ? 'bg-book-accent/15 text-book-accent border border-book-accent'
                                : 'bg-book-accent text-book-pageDark')}>
                        {complete && (
                            <span className="w-4 h-4 rounded-full bg-book-accent text-book-pageDark flex items-center justify-center">
                                <IconCheck className="w-2.5 h-2.5" />
                            </span>
                        )}
                        <span>{complete ? 'Done' : 'Complete'}</span>
                    </button>
                ) : (
                    // Practice activities own their own completion, so the bar keeps the
                    // space rather than letting Prev/Next jump position between views.
                    <span className="flex-grow" />
                )}

                <button type="button" onClick={onNext} disabled={!onNext}
                    aria-label={nextLabel ? 'Next: ' + nextLabel : 'Next'}
                    className="flex items-center justify-center w-11 h-11 rounded-full text-book-muted disabled:opacity-30 enabled:hover:text-book-accent transition-colors">
                    <Chevron dir="right" />
                </button>
            </div>
        </div>
    );
}
